// components/source/SourceBudgetProposalCard.tsx
import { memo, useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { ListPermissionPrompt, selectListPermission } from '@/components/list/ListPermissionPrompt';
import type { SourceBudgetProposal } from '#types/types';

export const SourceBudgetProposalCard = memo(({ initial, approval = false }: { initial: SourceBudgetProposal; approval?: boolean }) => {
  const { kernel, kernelReady } = useKernel();
  const runtime = useKernelStore(state => state.sourceBudgetProposals[initial.id]);
  const activeSessionId = useKernelStore(state => state.activeChatSessionId);
  const campaignId = useKernelStore(state => state.sourceSelectedCampaignId);
  const proposal = runtime?.proposal ?? initial;
  const inScope = activeSessionId === proposal.chatSessionId && campaignId === proposal.campaignId;
  const expired = Date.parse(proposal.expiresAt) <= Date.now();
  const pending = proposal.status === 'pending';

  useEffect(() => {
    if (!kernelReady || kernel.current?.getState().sourceBudgetProposals[initial.id]?.busy) return;
    void kernel.current?.sourceBudgetProposal(initial.campaignId, {
      action: 'read', chatSessionId: initial.chatSessionId, proposalId: initial.id,
    }).catch(() => undefined);
  }, [initial.id, initial.campaignId, initial.chatSessionId, kernelReady]);

  const decide = (action: 'accept' | 'decline') => {
    const state = kernel.current?.getState();
    const current = state?.sourceBudgetProposals[initial.id];
    const currentProposal = current?.proposal;
    if (selectListPermission(state?.activeChatSessionId ?? null)(state ?? { entityKernelState: {} })) return;
    if (!currentProposal || current.busy || currentProposal.status !== 'pending'
      || Date.parse(currentProposal.expiresAt) <= Date.now()
      || state?.activeChatSessionId !== currentProposal.chatSessionId
      || state?.sourceSelectedCampaignId !== currentProposal.campaignId) return;
    void kernel.current?.sourceBudgetProposal(currentProposal.campaignId, {
      action, chatSessionId: currentProposal.chatSessionId, proposalId: currentProposal.id,
    }).catch(() => undefined);
  };

  const summary = `${proposal.campaignName}: +${proposal.additionalCredits} credits; ceiling ${proposal.previousCeiling} → ${proposal.creditCeiling}; committed ${proposal.committedCredits}; target ${proposal.targetScore}/100. ${proposal.goal}`;
  if (approval) return <ListPermissionPrompt
    request={{ id: proposal.id, title: 'Approve this campaign credit increase?', label: 'Campaign', items: [{ id: proposal.campaignId, name: summary }] }}
    disabled={!runtime?.proposal || !pending || !inScope || expired || runtime?.busy}
    error={runtime?.error || (expired ? 'Expired. Request a new credit proposal.' : undefined)}
    onAnswer={decision => decide(decision === 'once' ? 'accept' : 'decline')}
  />;

  return <div data-source-budget-proposal={proposal.id} className="text-sm text-[#B0B0B0]">
    <p>{summary}</p>
    <p role="status" className="mt-2">{pending
      ? (expired ? 'Expired. Request a new credit proposal.' : 'Awaiting your approval below.')
      : proposal.status === 'declined' ? 'Declined. No additional credits authorised.'
        : proposal.message || (proposal.status === 'accepted' ? 'Approved. The new ceiling is authorised.' : 'The continuation failed.')}</p>
  </div>;
});

SourceBudgetProposalCard.displayName = 'SourceBudgetProposalCard';
